import { useEffect, useRef, useState } from 'react';
import { toastDuration } from '@vnetwork/margarita-shell/toast-options';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useMyContactDetail, useUpdateMyContact } from '@/api/customer';
import { submitWebsiteLead } from '@/api/leads';
import { useAuth } from '@/auth/AuthProvider';
import type { PdfContact } from './report-pdf';

interface PdfDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The scanned website, sent with the lead. */
  host: string;
  /** Called once the form is valid and the account update (if any) has settled. */
  onConfirm: (contact: PdfContact) => void;
}

// International numbers are fine here (the account may hold +84...): 8-15 digits.
const validPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, '').length;
  return digits >= 8 && digits <= 15;
};

/**
 * Asks for a name and phone number before the report can be downloaded, prefilled from the
 * account. Whatever the account has no value for is saved to it; what it already has is never
 * overwritten.
 */
export function PdfDetailsDialog({ open, onOpenChange, host, onConfirm }: PdfDetailsDialogProps) {
  const { user } = useAuth();
  const { show } = useToast();
  const { data: contact, isFetched } = useMyContactDetail();
  const update = useUpdateMyContact();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);

  // A name that is just the email's local part is a placeholder, not a name: shown blank, and
  // counted as missing.
  const emailLocal = (user?.email ?? '').split('@')[0]?.trim().toLowerCase() ?? '';
  const rawName = (contact?.contact_name || user?.name || '').trim();
  const seedName = rawName && !rawName.includes('@') && rawName.toLowerCase() !== emailLocal ? rawName : '';
  const seedPhone = (contact?.contact_phone ?? '').trim();

  // Seed once, after the contact query has settled (it is also "fetched" when it failed, so the
  // account name still fills in). This dialog mounts with the report, long before the contact
  // arrives, so seeding any earlier would lock in an empty phone.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !isFetched) return;
    seeded.current = true;
    setName(seedName);
    setPhone(seedPhone);
  }, [isFetched, seedName, seedPhone]);

  const nameError = touched && !name.trim() ? 'Enter your full name.' : undefined;
  const phoneError = touched && !validPhone(phone) ? 'Enter a valid phone number, e.g. 0987654321.' : undefined;

  const submit = async () => {
    setTouched(true);
    if (!name.trim() || !validPhone(phone)) return;

    // The update needs both fields and replaces the contact, so send the stored record with only
    // the empty ones filled in. No contact record (null) means there is nothing to update.
    const fillName = !seedName;
    const fillPhone = !seedPhone;
    if (contact && (fillName || fillPhone)) {
      try {
        await update.mutateAsync({
          ...contact,
          contact_name: fillName ? name.trim() : contact.contact_name,
          contact_phone: fillPhone ? phone.trim() : contact.contact_phone,
        });
      } catch {
        // The download does not depend on it.
        show({ tone: 'danger', duration: toastDuration('danger'), title: 'Could not save your details to your account' });
      }
    }
    // Same email as the signup lead, so the CMS merges this into it (it merges a later lead with
    // an email it already has). Best-effort: the download never waits on it.
    if (user?.email) {
      void submitWebsiteLead({
        firstname: name.trim(),
        email: user.email,
        phone: phone.trim(),
        website: host,
        specificNeeds: `VScanner report download: ${host}`,
      }).catch(() => {});
    }
    onConfirm({ name: name.trim(), phone: phone.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Download PDF report</DialogTitle>
          <DialogDescription>Confirm your contact details to download the report.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <DialogBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Field label="Name" required error={nameError}>
                {(p) => <Input {...p} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />}
              </Field>
              <Field label="Phone" required error={phoneError}>
                {(p) => (
                  <Input {...p} type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                )}
              </Field>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
